import React, { useState, useEffect } from "react";
import { FiBell, FiUser, FiMenu, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../../firebase";
import { collection, query, where, getDocs, limit } from "firebase/firestore";
import "./usercss/global.css";
import auravitLogo from "../../images/auravit_logo.png";
import cfsiLogo from "../../images/CFSI-mobile-logo-sr.png";
import { useLanguage } from "../../contexts/LanguageContext";
import { translations } from "../../translations";

export default function UserTopNavBar({ activeTab, setActiveTab }) {
  const navigate = useNavigate();
  const [hasPendingAccess, setHasPendingAccess] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    let mounted = true;
    
    const checkPendingAccess = async () => {
      if (!mounted) return;
      
      try {
        const q = query(
          collection(db, 'appointments'),
          where('uid', '==', user.uid),
          limit(50)
        );
        
        const snapshot = await getDocs(q);
        if (mounted) {
          const hasPending = snapshot.docs.some((appointmentDoc) =>
            Object.values(appointmentDoc.data().dataAccessRequests || {})
              .some(request => request.status === 'PENDING')
          );
          setHasPendingAccess(hasPending);
        }
      } catch (err) {
        console.error("Error checking pending access:", err);
      }
    };

    // Initial check
    checkPendingAccess();
    
    // Check every 60 seconds instead of continuous listener
    const interval = setInterval(checkPendingAccess, 60000);
    
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      <header className="top-nav">
        {/* Logo */}
        <div className="nav-left">
          <div className="nav-logos">
            <img src={auravitLogo} alt="AuraVit" className="nav-logo auravit" />
            <span className="logo-separator">|</span>
            <img src={cfsiLogo} alt="CFSI" className="nav-logo cfsi" />
          </div>
        </div>

        {/* Center Navigation - Desktop */}
        <nav className="nav-center">
          <button
            className={`nav-link ${activeTab === "learn" ? "active" : ""}`}
            onClick={() => setActiveTab("learn")}
          >
            {t.learn}
          </button>

          <button
            className={`nav-link ${activeTab === "askai" ? "active" : ""}`}
            onClick={() => setActiveTab("askai")}
          >
            {t.askAuri}
          </button>

          
          <button
            className={`nav-link ${activeTab === "records" ? "active" : ""}`}
            onClick={() => setActiveTab("records")}
          >
            {t.records}
          </button>

          <button
            className={`nav-link ${activeTab === "appointment" ? "active" : ""}`}
            onClick={() => setActiveTab("appointment")}
          >
            {t.appointment}
          </button>
        </nav>

        {/* Right Icons */}
        <div className="nav-right">
          {/* Mobile Menu Button */}
          <button
            className="icon-btn mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Open Menu"
          >
            <FiMenu />
          </button>

          {/* Notifications */}
          <button
            className="icon-btn circle"
            onClick={() => setActiveTab("notifications")}
            aria-label="Notifications"
          >
            <FiBell />
            {hasPendingAccess && <span className="notif-dot" />}
          </button>

          {/* Profile (now a panel like notifications) */}
          <button
            className="icon-btn"
            onClick={() => setActiveTab("profile")}
            aria-label="User Profile"
          >
            <FiUser />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}

      {/* Mobile Menu Drawer */}
      <div className={`mobile-menu-drawer ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <div className="mobile-menu-logos">
            <img src={auravitLogo} alt="AuraVit" className="nav-logo auravit" />
            <span className="logo-separator">|</span>
            <img src={cfsiLogo} alt="CFSI" className="nav-logo cfsi" />
          </div>
          <button
            className="mobile-menu-close"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Close Menu"
          >
            <FiX />
          </button>
        </div>
        
        <nav className="mobile-menu-nav">
          <button
            className={`mobile-menu-link ${activeTab === "learn" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("learn");
              setIsMobileMenuOpen(false);
            }}
          >
            {t.learn}
          </button>

          <button
            className={`mobile-menu-link ${activeTab === "askai" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("askai");
              setIsMobileMenuOpen(false);
            }}
          >
            {t.askAuri}
          </button>

          <button
            className={`mobile-menu-link ${activeTab === "records" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("records");
              setIsMobileMenuOpen(false);
            }}
          >
            {t.records}
          </button>

          <button
            className={`mobile-menu-link ${activeTab === "appointment" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("appointment");
              setIsMobileMenuOpen(false);
            }}
          >
            {t.appointment}
          </button>

          <button
            className={`mobile-menu-link ${activeTab === "notifications" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("notifications");
              setIsMobileMenuOpen(false);
            }}
          >
            {t.notifications}
          </button>

          <button
            className={`mobile-menu-link ${activeTab === "profile" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("profile");
              setIsMobileMenuOpen(false);
            }}
          >
            {t.profile}
          </button>
        </nav>
      </div>
    </>
  );
}
